import {
  presets,
  type DateFilterBounds,
  type DatePreset,
  type DateRangeValue,
} from '@flintmint/web-shared/components/date-picker';
import type { SidebarAppliedFilters, SidebarFilterParamKeys } from './types';
import {
  datePresetFilterKey,
  excludedTagIdsFilterKey,
  includedTagIdsFilterKey,
  updatedSinceFilterKey,
  updatedToFilterKey,
} from '@/constants/search-filters';

export const defaultSidebarFilterParamKeys: SidebarFilterParamKeys = {
  includedTagIds: includedTagIdsFilterKey,
  excludedTagIds: excludedTagIdsFilterKey,
  updatedSince: updatedSinceFilterKey,
  updatedTo: updatedToFilterKey,
  datePreset: datePresetFilterKey,
};

const normalizeTagIds = (ids: string[]): string[] => [...new Set(ids.map((id) => id.trim()).filter(Boolean))].sort();

const parseTagIds = (value: string | null): string[] => normalizeTagIds((value ?? '').split(','));

const serializeTagIds = (ids: string[]): string | null => {
  const normalized = normalizeTagIds(ids);
  return normalized.length ? normalized.join(',') : null;
};

const parseTimestamp = (value: string | null): number | null => {
  const n = value == null || value === '' ? NaN : Number(value);
  const isValid = Number.isInteger(n) && Number.isFinite(n) && n >= 0;
  return isValid ? n : null;
};

const orderRange = (start: number | null, end: number | null): DateFilterBounds => {
  const isInverted = start != null && end != null && start > end;
  return isInverted ? [end, start] : [start, end];
};

const setOrDelete = (params: URLSearchParams, key: string, value: string | null) => {
  if (value != null) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
};

// ─── Date helpers ─────────────────────────────────────────────────────────────

export const hasDateSelection = (date: DateRangeValue | null): date is DateRangeValue => {
  if (!date) return false;
  const [start, end] = date.range;
  return date.preset != null || start != null || end != null;
};

const parseDatePreset = (param: string | null): DatePreset => {
  if (param == null) return null;
  const isValid = presets.some((p) => p.value === param);

  return isValid ? (param as Exclude<DatePreset, null>) : null;
};

const resolveDateRange = (preset: DatePreset, rawStart: number | null, rawEnd: number | null): DateFilterBounds => {
  const hasExplicitRange = rawStart != null || rawEnd != null;

  if (!hasExplicitRange && !!preset) {
    return presets.find((p) => p.value === preset)?.getRange() ?? [null, null];
  }

  return orderRange(rawStart, rawEnd);
};

export const parseFiltersFromParams = (
  params: URLSearchParams,
  keys: SidebarFilterParamKeys = defaultSidebarFilterParamKeys
): SidebarAppliedFilters => {
  const preset = parseDatePreset(params.get(keys.datePreset));
  const rawStart = parseTimestamp(params.get(keys.updatedSince));
  const rawEnd = parseTimestamp(params.get(keys.updatedTo));

  const [rangeStart, rangeEnd] = resolveDateRange(preset, rawStart, rawEnd);
  const dateCandidate: DateRangeValue = {
    preset,
    range: [rangeStart, rangeEnd] as DateFilterBounds,
  };
  const date = hasDateSelection(dateCandidate)
    ? {
        preset: dateCandidate.preset,
        range: dateCandidate.range,
      }
    : null;

  return {
    includedTagIds: parseTagIds(params.get(keys.includedTagIds)),
    excludedTagIds: parseTagIds(params.get(keys.excludedTagIds)),
    date,
  };
};

export const writeFiltersToParams = (
  params: URLSearchParams,
  value: SidebarAppliedFilters,
  keys: SidebarFilterParamKeys = defaultSidebarFilterParamKeys
) => {
  setOrDelete(params, keys.includedTagIds, serializeTagIds(value.includedTagIds));
  setOrDelete(params, keys.excludedTagIds, serializeTagIds(value.excludedTagIds));

  if (!hasDateSelection(value.date)) {
    [keys.datePreset, keys.updatedSince, keys.updatedTo].forEach((k) => params.delete(k));
    return;
  }

  const [start, end] = orderRange(value.date.range[0], value.date.range[1]);
  setOrDelete(params, keys.datePreset, value.date.preset);
  setOrDelete(params, keys.updatedSince, start != null ? String(start) : null);
  setOrDelete(params, keys.updatedTo, end != null ? String(end) : null);
};

export const clearFiltersInParams = (params: URLSearchParams, keys: SidebarFilterParamKeys = defaultSidebarFilterParamKeys) => {
  Object.values(keys).forEach((k) => params.delete(k));
};
